import React, { useState, useEffect } from 'react';

const Brands = () => {
  // Each brand now has a priority tag. Higher number = higher priority.
  // Reorder by changing the priority numbers below.
  // Optional: Add zoom property for SVG images (default: 1). Higher = more zoomed in.
  const brands = [
    { name: 'Caterpillar', priority: 100 },
    { name: 'Komatsu', priority: 90 },
    { name: 'Volvo', priority: 80, zoom: 2.3 },
    { name: 'JCB', priority: 70 },
    { name: 'Hitachi', priority: 70 },
    { name: 'Hyundai Heavy', priority: 60 },
    { name: 'Liebherr', priority: 60 },
    { name: 'Doosan', priority: 50 },
    { name: 'John Deere', priority: 50 },
    { name: 'Case', priority: 45 },
    { name: 'Bobcat', priority: 40 },
    { name: 'XCMG', priority: 35 },
    { name: 'Dynapac', priority: 30 },
    { name: 'Terex', priority: 30 },
    { name: 'Manitou', priority: 25 },
    { name: 'Kobelco', priority: 25 },
    { name: 'Sumitomo', priority: 20 },
    { name: 'Sany', priority: 20, zoom: 2.5 },
    { name: 'Zoomlion', priority: 15 },
    { name: 'Sandvik', priority: 10 },
    { name: 'Atlas Copco', priority: 10 },
    { name: 'Wirtgen', priority: 5 },
    { name: 'Bomag', priority: 5 },
    { name: 'Hamm', priority: 5 },
    { name: 'Vogele', priority: 5 },
    { name: 'Tadano', priority: 5 },
    { name: 'Manitowoc', priority: 5, zoom: 1.8 },
    { name: 'Grove', priority: 5, zoom: 2.1 },
    { name: 'Demag', priority: 5 },
    { name: 'Scania', priority: 0 },
    { name: 'MAN', priority: 0 },
    { name: 'Mercedes-Benz', priority: 0 },
    { name: 'Iveco', priority: 0 },
    { name: 'Mack', priority: 0 },
    { name: 'Hino', priority: 0 },
    { name: 'Isuzu', priority: 0 },
    { name: 'UD Trucks', priority: 0 },
    { name: 'Fuso', priority: 0 },
    { name: 'Tata', priority: 0 },
    { name: 'Ashok Leyland', priority: 0 },
    { name: 'Eicher', priority: 0 },
    { name: 'BharatBenz', priority: 0 }
  ];

  const slugify = (name) =>
    name.replace(/[^a-zA-Z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const BrandItem = ({ brand }) => {
    const { name, priority, zoom = 1 } = brand;
    const slug = slugify(name);
    const [src, setSrc] = useState(`/logos/${slug}.svg`);
    const [failed, setFailed] = useState(false);
    const [isSvg, setIsSvg] = useState(true);
    const [triedPng, setTriedPng] = useState(false);

    useEffect(() => {
      setSrc(`/logos/${slug}.svg`);
      setIsSvg(true);
      setFailed(false);
      setTriedPng(false);
    }, [slug]);

    const handleImageError = () => {
      if (isSvg && !triedPng) {
        setSrc(`/logos/${slug}.png`);
        setIsSvg(false);
        setTriedPng(true);
        return;
      }
      setFailed(true);
    };

    return (
      <div
        className="relative group bg-white 2-48 h-28 p-6 rounded-lg border-2 border-gray-200 hover:border-blue-500 transition-all duration-300 hover:shadow-lg flex items-center justify-center text-center cursor-pointer transform hover:-translate-y-1 overflow-hidden"
        title={`${name} (priority: ${priority})`}
      >
        <div className="w-full h-full flex items-center justify-center">
          {!failed ? (
            <img
              src={src}
              alt={name + ' logo'}
              className="max-h-12"
              onError={handleImageError}
              style={
                isSvg && zoom !== 1
                  ? {
                      width: zoom > 1 ? `calc(100% / ${zoom})` : '100%',
                      transform: `scale(${zoom})`,
                      transformOrigin: 'center',
                    }
                  : undefined
              }
            />
          ) : (
            <h3 className="text-sm sm:text-base font-semibold text-gray-700 group-hover:text-blue-600 transition-colors">
              {name}
            </h3>
          )}
        </div>
      </div>
    );
  };

  // sort by priority desc, then name asc
  const sortedBrands = [...brands].sort((a, b) => {
    if (b.priority !== a.priority) return b.priority - a.priority;
    return a.name.localeCompare(b.name);
  });

  return (
    <section id="brands" className="py-20 sm:py-28 bg-slate-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Brands We Support
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            We supply genuine and aftermarket parts for all major heavy machinery 
            and commercial vehicle brands. Whatever your equipment needs, we have you covered.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {sortedBrands.map((brand) => (
            <BrandItem key={slugify(brand.name)} brand={brand} />
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-gray-600 text-base">
            Don't see your brand? <a href="#contact" className="text-blue-600 font-semibold">Contact us</a> - we likely have it in stock!
          </p>
        </div>
      </div>
    </section>
  );
};

export default Brands;